import { and as kqlAnd, not as kqlNot, or as kqlOr } from './kql';

/**
 * ONE LEVEL OF GROUPING over a builder's rows (KPR-951, KPE-010).
 *
 * The builders show a query as rows of field, comparison and value. Since
 * KPR-951 the rows can be joined two ways and gathered one level deep, the
 * pattern people know from other tools:
 *
 *   Match [all | any] of these          the top line: `and` or `or`
 *     <row>
 *     [all | any | none] of these       a group: `(a and b)`, `(a or b)`, or
 *       <row>                           `not (a or b)` — none of them holds
 *       <row>
 *
 * This module is the shape and nothing else. It never builds query text and
 * never knows what a row holds: a builder passes its own `readRow(node)` (a
 * node -> its row, or null when no row shows it) and `rowToNode(row)` (a row ->
 * its node, or null while the row is incomplete). ConditionBuilder is the
 * first caller; FilterBuilder's half (KPR-978) reuses it with its own rows, so
 * the two group the same way and a query written in one opens in the other.
 *
 * WHAT ONE LEVEL READS. The top node is a row, an `and`, an `or`, or a group on
 * its own. Each of its arguments is a row or a group, and a group is:
 *
 *   all    an `and` of rows
 *   any    an `or` of rows
 *   none   `not` of an `or` of rows, or `not` of one row
 *
 * Anything else — a group inside a group, `not (a and b)` ("not all of these",
 * which the builders do not offer), a `not` inside a group — is deeper than the
 * builder shows, and reads as null. The builder then shows the text another way
 * (a read-only sentence) and never rewrites it: a builder that re-serialized
 * the part it understood would delete the rest.
 *
 * WHAT CANONICAL TEXT CANNOT KEEP. The grammar flattens `a and (b and c)` into
 * `a and b and c`, and a group of one row is just that row. So a group whose
 * join is the top line's, or that holds one row, reopens as plain rows. It
 * means exactly the same; only the drawing differs.
 */

export const MATCH_ALL = 'all';
export const MATCH_ANY = 'any';
export const MATCH_NONE = 'none';

/** What the top line offers, and what a group offers. */
export const TOP_MATCHES = Object.freeze([MATCH_ALL, MATCH_ANY]);
export const GROUP_MATCHES = Object.freeze([MATCH_ALL, MATCH_ANY, MATCH_NONE]);

let groupSeq = 0;
const nextGroupId = () => `condition-group-${++groupSeq}`;

/** Is this item of the top line a group (rather than a row)? */
export const isGroupItem = item => !!item && item.group === true;

/** A new group: `{ id, group: true, match, rows }`. */
export function makeGroup(match = MATCH_ANY, rows = []) {
	return { id: nextGroupId(), group: true, match: GROUP_MATCHES.includes(match) ? match : MATCH_ANY, rows };
}

/** The join a new group should start with: the other one, since a group that joins like its parent adds nothing. */
export const groupMatchFor = topMatch => (topMatch === MATCH_ANY ? MATCH_ALL : MATCH_ANY);

/** Every row, the ones inside groups included, in reading order. */
export function allRows(items) {
	const out = [];
	(items || []).forEach((item) => {
		if (isGroupItem(item)) out.push(...(item.rows || []));
		else if (item) out.push(item);
	});
	return out;
}

/**
 * Items with `fn(row)` applied to every row, top-level and grouped. An item
 * that comes back unchanged keeps its identity, and so does the array when
 * nothing changed.
 */
export function mapRows(items, fn) {
	let changed = false;
	const next = (items || []).map((item) => {
		if (isGroupItem(item)) {
			let inner = false;
			const rows = (item.rows || []).map((row) => {
				const mapped = fn(row);
				if (mapped !== row) inner = true;
				return mapped;
			});
			if (!inner) return item;
			changed = true;
			return { ...item, rows };
		}
		const mapped = fn(item);
		if (mapped !== item) changed = true;
		return mapped;
	});
	return changed ? next : items;
}

/** Items without the row `rowId`, wherever it sits (a group it leaves stays, even empty). */
export function removeRow(items, rowId) {
	return (items || [])
		.filter(item => isGroupItem(item) || item?.id !== rowId)
		.map(item => (isGroupItem(item) && (item.rows || []).some(row => row.id === rowId)
			? { ...item, rows: item.rows.filter(row => row.id !== rowId) }
			: item));
}

/** `fn(group)` applied to the group `groupId`. */
export function updateGroup(items, groupId, fn) {
	return (items || []).map(item => (isGroupItem(item) && item.id === groupId ? fn(item) : item));
}

/** A group's rows: `{ match, rows }`, or null when the node is not one level of rows. */
function readGroupNode(node, readRow) {
	if (!node) return null;
	const rowsOf = (args) => {
		const rows = (args || []).map(arg => readRow(arg));
		return rows.length && rows.every(Boolean) ? rows : null;
	};
	if (node.op === 'and' || node.op === 'or') {
		const rows = rowsOf(node.args);
		return rows ? { match: node.op === 'and' ? MATCH_ALL : MATCH_ANY, rows } : null;
	}
	if (node.op === 'not') {
		const inner = node.arg;
		if (inner && inner.op === 'or') {
			const rows = rowsOf(inner.args);
			return rows ? { match: MATCH_NONE, rows } : null;
		}
		// `not (a and b)` is "not all of these": not offered, so deeper —
		// unless the `and` is ONE row of the builder's (FilterBuilder's date
		// span, `created >= … and created < …`, KPR-978), which is "none of"
		// that one row. Any other `not` reads only as one row the builder
		// shows on its own (a group of one `has no entry where…` row writes
		// `not not g has (…)`).
		if (!inner) return null;
		const row = readRow(inner);
		if (!row && inner.op === 'and') return null;
		return row ? { match: MATCH_NONE, rows: [row] } : null;
	}
	return null;
}

/**
 * A parsed query as the top line and its items: `{ match, items }`, or null
 * when it is deeper than one level. `readRow(node)` is tried first on every
 * node (a builder may show a `not …` as one row of its own, as a repeating
 * group's `has no entry where…` is), and a group is read only when it is not.
 * No query (null) is no items.
 */
export function readGrouped(ast, readRow) {
	if (!ast) return { match: MATCH_ALL, items: [] };
	const readItem = (node) => {
		const row = readRow(node);
		if (row) return row;
		const group = readGroupNode(node, readRow);
		return group ? makeGroup(group.match, group.rows) : null;
	};
	if (ast.op === 'and' || ast.op === 'or') {
		const items = (ast.args || []).map(readItem);
		if (!items.length || !items.every(Boolean)) return null;
		return { match: ast.op === 'and' ? MATCH_ALL : MATCH_ANY, items };
	}
	const item = readItem(ast);
	return item ? { match: MATCH_ALL, items: [item] } : null;
}

/** One group as a node, or null while none of its rows is complete. */
export function groupToNode(group, rowToNode) {
	const nodes = (group?.rows || []).map(rowToNode).filter(Boolean);
	if (!nodes.length) return null;
	if (group.match === MATCH_NONE) return kqlNot(nodes.length === 1 ? nodes[0] : kqlOr(nodes));
	if (nodes.length === 1) return nodes[0];
	return group.match === MATCH_ANY ? kqlOr(nodes) : kqlAnd(nodes);
}

/** The top line and its items as one node, or null while nothing is complete. */
export function writeGrouped(items, match, rowToNode) {
	const nodes = (items || [])
		.map(item => (isGroupItem(item) ? groupToNode(item, rowToNode) : rowToNode(item)))
		.filter(Boolean);
	if (!nodes.length) return null;
	if (nodes.length === 1) return nodes[0];
	return match === MATCH_ANY ? kqlOr(nodes) : kqlAnd(nodes);
}
